import React, { useCallback } from "react";
import { toast as hotToast, useToasterStore } from "react-hot-toast";
import { Box, Text, Icon, CloseButton, ScaleFade } from "@chakra-ui/react";
import { InformationCircleIcon, CheckCircleIcon, ExclamationTriangleIcon, ExclamationCircleIcon, XMarkIcon, } from "@heroicons/react/24/outline";
import { Button } from "./button";
const timers = new Map();
export function useToast() {
    const store = useToasterStore();
    const toast = useCallback((options) => {
        const isToastExists = store.toasts.find((existingToast) => {
            return existingToast.id === options.id && existingToast.visible;
        });
        if (isToastExists) {
            hotToast.dismiss(options.id);
        }
        const show = () => {
            hotToast.custom((toastInstance) => (React.createElement(Toast, { ...options, isVisible: toastInstance.visible, onClose: () => {
                    hotToast.dismiss(toastInstance.id);
                } })), {
                id: options.id,
            });
        };
        if (!isToastExists) {
            show();
            return;
        }
        if (timers.has(options.id)) {
            clearTimeout(timers.get(options.id));
        }
        const timer = setTimeout(show, 200);
        timers.set(options.id, timer);
    }, [store]);
    const close = useCallback((id) => {
        hotToast.dismiss(id);
    }, []);
    const closeAll = useCallback(() => {
        hotToast.dismiss();
    }, []);
    return {
        toast,
        close,
        closeAll,
    };
}
function Toast({ isVisible, title, message, variant, actionText, onAction, onClose, }) {
    let color = "blue";
    let icon = InformationCircleIcon;
    if (variant === "success") {
        color = "green";
        icon = CheckCircleIcon;
    }
    if (variant === "warning") {
        color = "yellow";
        icon = ExclamationTriangleIcon;
    }
    if (variant === "error") {
        color = "red";
        icon = ExclamationCircleIcon;
    }
    const actionClicked = useCallback(() => {
        onClose();
        if (typeof onAction === "function") {
            onAction();
        }
    }, [onAction, onClose]);
    return (React.createElement(ScaleFade, { initialScale: 0.9, in: isVisible },
        React.createElement(Box, { display: "flex", alignItems: message ? "start" : "center", gap: 16, bg: "white", borderWidth: 1, borderColor: "gray.300", borderRadius: "lg", p: 4, shadow: "lg" },
            React.createElement(Box, { display: "flex", alignItems: message ? "start" : "center", gap: 3 },
                React.createElement(Icon, { flex: "none", as: icon, color: `${color}.800`, boxSize: 6 }),
                React.createElement(Box, { flex: "1", minWidth: 0, maxWidth: "360px" },
                    React.createElement(Text, { color: `${color}.800`, fontSize: "md", fontWeight: "medium" }, title),
                    message && (React.createElement(Text, { mt: 1, color: "gray.600", fontSize: "md", lineHeight: "tall" }, message)),
                    message && actionText && (React.createElement(Box, { mt: 3 },
                        React.createElement(Button, { onClick: actionClicked }, actionText))))),
            React.createElement(Box, { display: "flex", alignItems: "center", gap: 3 },
                !message && actionText && (React.createElement(Button, { onClick: actionClicked }, actionText)),
                React.createElement(CloseButton, { flex: "none", "aria-label": "Dismiss", sx: {
                        "--close-button-size": "20px",
                    }, onClick: onClose },
                    React.createElement(Icon, { as: XMarkIcon, boxSize: 4, color: "gray.800" }))))));
}
